import { useState, useMemo, useCallback } from 'react';
import { NewDropdown, Typography } from 'app/components/ui';
import { DocumentTaxonomyConfigCurrency, TagUnit, useCreateUnit, useUnitsByDocument, useUpdateTag } from 'app/api/tag-queries';
import { UnitModal, UnitSpec } from 'app/components/units/UnitModal';
import { buildUnitOptions, unitLabel } from 'app/utils/unit-labels';
import { i18n } from 'app/utils/i18n';

interface CurrencyFieldProps {
  tagId: string;
  documentSuperId: number;
  selectedCurrency: string | null;
  currencySelections: DocumentTaxonomyConfigCurrency[];
  unit?: TagUnit | null;
  configId?: string;
  disabled?: boolean;
}

export const CurrencyField = ({ tagId, documentSuperId, selectedCurrency, currencySelections, unit, disabled = false }: CurrencyFieldProps) => {
  const [showUnitModal, setShowUnitModal] = useState(false);

  const { mutate: updateTag } = useUpdateTag();
  const { mutateAsync: createUnit } = useCreateUnit();
  const { data: documentUnits } = useUnitsByDocument(documentSuperId);

  const unitDisplay = unit ? unitLabel(unit) : null;

  const options = useMemo(() => buildUnitOptions(currencySelections, documentUnits), [currencySelections, documentUnits]);

  const handleSelect = useCallback(
    (value: string) => {
      if (value.startsWith('unit:')) {
        updateTag({ id: tagId, documentSuperId, unitId: value.slice('unit:'.length) });
      } else {
        updateTag({ id: tagId, currency: value, documentSuperId });
      }
    },
    [tagId, documentSuperId, updateTag]
  );

  const handleUnitSave = useCallback(
    async (spec: UnitSpec, applyToTag: boolean) => {
      // No client id -> the backend find-or-creates, so repeated specs dedupe to one row.
      const created = await createUnit({ documentSuperId, ...spec });
      if (applyToTag && created?.id) {
        updateTag({ id: tagId, documentSuperId, unitId: created.id });
      }
    },
    [createUnit, updateTag, tagId, documentSuperId]
  );

  return (
    <>
      <div className="property-row">
        <Typography fontWeight="semibold" className="property-label">
          {i18n('tag.monetary.label')}
        </Typography>
        {disabled ? (
          <Typography variant="body-7" fontWeight="light">
            {unitDisplay ?? selectedCurrency ?? '-'}
          </Typography>
        ) : (
          <NewDropdown
            fullwidth
            options={options}
            currentValue={unitDisplay ?? selectedCurrency ?? i18n('tag.monetary.choose')}
            onUpdate={handleSelect}
            customOption={
              <button onClick={() => setShowUnitModal(true)} className="flex-row custom-option-btn">
                <i className="fa-regular fa-circle-plus" />
                <Typography variant="body-5" fontWeight="light" color="#1E2533">
                  {i18n('tag.create.custom.value')}
                </Typography>
              </button>
            }
          />
        )}
      </div>

      {!disabled && (
        <UnitModal
          show={showUnitModal}
          defaultCurrency={selectedCurrency ?? currencySelections.find((c) => c.isActive)?.currency ?? null}
          showApplyToggle
          onClose={() => setShowUnitModal(false)}
          onSave={handleUnitSave}
        />
      )}
    </>
  );
};
